import {useTranslation} from "react-i18next";
import {useHistory, useLocation} from "react-router-dom";

import {FilterOfferType} from "../../web/src/common/app/constants/enums";
import {chartsRoutes, checkIfIsChartsCityView} from "../../web/src/common/app/routing/charts_routes";
import {mapPathnameToOfferType, OfferType} from "../../web/src/common/app/routing/offer_type";

export const usePropertyTypeFilter = () => {
    const history = useHistory();
    const {pathname} = useLocation();

    const {
        i18n: {language}
    } = useTranslation();

    const activeFilter = (() => {
        switch (true) {
            case pathname.includes(chartsRoutes[language].flats.root): {
                return FilterOfferType.FLATS;
            }
            case pathname.includes(chartsRoutes[language].houses.root): {
                return FilterOfferType.HOUSES;
            }
            default:
                return undefined;
        }
    })();

    function transformPath(pathname: string, language: string) {
        const offerType = mapPathnameToOfferType(pathname);
        const isFlatOfferType = offerType === OfferType.FLAT;
        const detailedViewsRouteChunks = [
            {
                chunk: language === "pl" ? "podsumowanie-lokalizacji" : "location-summary",
                route: isFlatOfferType
                    ? chartsRoutes[language].properties.city.locationSummary
                    : chartsRoutes[language].flats.city.locationSummary
            },
            {
                chunk: language === "pl" ? "ceny" : "prices",
                route: isFlatOfferType
                    ? chartsRoutes[language].properties.city.prices
                    : chartsRoutes[language].flats.city.prices
            },
            {
                chunk: language === "pl" ? "oferta" : "offer",
                route: isFlatOfferType
                    ? chartsRoutes[language].properties.city.offer
                    : chartsRoutes[language].flats.city.offer
            },
            {
                chunk: language === "pl" ? "popyt" : "demand",
                route: isFlatOfferType
                    ? chartsRoutes[language].properties.city.demand
                    : chartsRoutes[language].flats.city.demand
            },
            {
                chunk: language === "pl" ? "sprzedane" : "sold",
                route: isFlatOfferType
                    ? chartsRoutes[language].properties.city.sold
                    : chartsRoutes[language].flats.city.sold
            },
            {
                chunk: language === "pl" ? "nowe" : "added",
                route: isFlatOfferType
                    ? chartsRoutes[language].properties.city.added
                    : chartsRoutes[language].flats.city.added
            }
        ];

        const defaultRoute = chartsRoutes[language].flats.city.root;
        const pathnameChunks = pathname.split("/").filter(Boolean);

        if (pathnameChunks.length > 0) {
            const lastChunk = pathnameChunks[pathnameChunks.length - 1];
            const matchingRoute = detailedViewsRouteChunks.find(({chunk}) => lastChunk === chunk);

            if (matchingRoute) {
                return matchingRoute.route;
            }

            return "";
        }

        return defaultRoute;
    }

    const filter = (clickedFilter?: FilterOfferType) => {
        const isCityView = checkIfIsChartsCityView(pathname, language);

        const targetPath = () => {
            switch (true) {
                case clickedFilter === FilterOfferType.FLATS && isCityView: {
                    return pathname.includes(chartsRoutes[language].houses.root)
                        ? chartsRoutes[language].properties.city.root
                        : chartsRoutes[language].houses.city.root;
                }
                case clickedFilter === FilterOfferType.HOUSES && isCityView: {
                    return pathname.includes(chartsRoutes[language].flats.root)
                        ? chartsRoutes[language].properties.city.root
                        : chartsRoutes[language].flats.city.root;
                }
                case !clickedFilter && isCityView: {
                    return chartsRoutes[language].properties.city.root;
                }
                case clickedFilter === FilterOfferType.FLATS && !isCityView: {
                    return pathname.includes(chartsRoutes[language].houses.root)
                        ? chartsRoutes[language].properties.root
                        : chartsRoutes[language].houses.root;
                }
                case clickedFilter === FilterOfferType.HOUSES && !isCityView: {
                    return pathname.includes(chartsRoutes[language].flats.root)
                        ? chartsRoutes[language].properties.root
                        : chartsRoutes[language].flats.root;
                }
                case !clickedFilter && !isCityView: {
                    return chartsRoutes[language].properties.root;
                }
                default: {
                    return chartsRoutes[language].properties.root;
                }
            }
        };

        const compiledPath = targetPath();
        const transformedPath = transformPath(pathname || "", language);
        const path = transformedPath || compiledPath;

        history.push(path);
    };

    return {
        activeFilter,
        filter
    };
};
